/**
 * Client-side helper for /api/analytics/funnel-event. Track 3.13-phase-0.
 *
 * Generates + persists a per-browser-tab funnel session id (sessionStorage)
 * so events on one funnel walk correlate before an account exists. Server
 * side, `user_id` is populated automatically if a Better Auth session
 * already exists (pending or otherwise).
 *
 * fireFunnelEvent uses fetch with keepalive:true so events fired during
 * navigation away (e.g. final processing → /checkout/plan redirect) still
 * dispatch. Failures are swallowed — analytics loss is acceptable; funnel
 * UX is not.
 */

const SESSION_STORAGE_KEY = 'feelgood-funnel-session';

function readOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // SessionStorage can throw in some privacy modes — fall through to a
    // per-page-load id rather than block analytics entirely.
    return crypto.randomUUID();
  }
}

export interface FunnelEventPayload {
  eventType: 'impression' | 'cta_continue' | 'processing_auto_complete';
  slot: string;
  slideId: string;
  anchorQuestionId: string;
  displayNumber: number;
}

export function fireFunnelEvent(payload: FunnelEventPayload): void {
  if (typeof window === 'undefined') return;
  const sessionId = readOrCreateSessionId();
  const body = JSON.stringify({ ...payload, sessionId });
  try {
    fetch('/api/analytics/funnel-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {
      // Swallow — loss is acceptable.
    });
  } catch {
    // Older browsers without keepalive support throw synchronously — also fine to swallow.
  }
}
